import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { VenueCard } from "@/components/venues/VenueCard";
import { SortSelect } from "@/components/venues/SortSelect";
import { useVenues } from "@/hooks/useVenues";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import type { SearchFiltersState } from "@/components/venues/SearchFilters";
import { GlobalSchema } from "@/components/seo/GlobalSchema";
import { JsonLd } from "@/components/seo/JsonLd";
import { breadcrumbSchema, collectionPageSchema } from "@/components/seo/schemas";
import { useSchemaIndexable } from "@/components/seo/useSchemaIndexable";

export default function CityVenues() {
  const { city } = useParams<{ city: string }>();
  const navigate = useNavigate();
  const rawParam = decodeURIComponent(city || "");

  // Redirect uppercase to lowercase
  useEffect(() => {
    if (city && city !== city.toLowerCase()) {
      navigate(`/salas/${city.toLowerCase()}/`, { replace: true });
    }
  }, [city, navigate]);

  // Resolve city by slug (lowercase) or fallback to name match
  const { data: cityRecord, isLoading: cityLoading } = useQuery({
    queryKey: ["city-resolve", rawParam.toLowerCase()],
    queryFn: async () => {
      // Try slug first
      const { data: bySlug } = await supabase
        .from("cities")
        .select("name, slug, hero_image_url, hero_image_alt, hero_image_credit")
        .eq("slug", rawParam.toLowerCase())
        .eq("is_active", true)
        .single();
      if (bySlug) return bySlug;

      // Fallback: match by name (for old URLs with raw city name)
      const { data: byName } = await supabase
        .from("cities")
        .select("name, slug, hero_image_url, hero_image_alt, hero_image_credit")
        .ilike("name", rawParam)
        .eq("is_active", true)
        .single();
      if (byName && byName.slug !== rawParam.toLowerCase()) {
        // Redirect to canonical slug URL
        navigate(`/salas/${byName.slug}/`, { replace: true });
      }
      return byName;
    },
    staleTime: 1000 * 60 * 10,
  });

  const cityName = cityRecord?.name ?? rawParam;

  useSeoMeta({
    pageType: "city_detail",
    variables: { ciudad: cityName },
    canonicalPath: cityRecord ? `/salas/${cityRecord.slug}/` : undefined,
  });

  const [sortBy, setSortBy] = useState("name");
  const [page, setPage] = useState(0);

  const filters: SearchFiltersState = {
    name: "",
    city: cityName,
    community: "",
    minCapacity: "",
    maxOfficialPrice: "",
    maxCommunityPrice: "",
    type: "",
    sortBy,
  };

  const { data, isLoading } = useVenues(filters, page);
  const isIndexable = useSchemaIndexable("city_detail");

  const citySchemaData = useMemo(() => {
    if (!isIndexable || !data?.venues?.length || !cityRecord) return null;
    const canonicalPath = `/salas/${cityRecord.slug}/`;
    const breadcrumbs = breadcrumbSchema([
      { name: "Inicio", path: "/" },
      { name: "Salas", path: "/salas/" },
      { name: cityName, path: canonicalPath },
    ]);
    const collection = collectionPageSchema(
      `Salas de Conciertos en ${cityName}`,
      canonicalPath,
      data.venues,
      { "@type": "City", name: cityName, url: `https://bandflow.es${canonicalPath}` }
    );
    return [breadcrumbs, collection];
  }, [isIndexable, data?.venues, cityRecord, cityName]);

  return (
    <Layout>
      <GlobalSchema pageType="city_detail" />
      {citySchemaData && <JsonLd data={citySchemaData} />}
      <section
        className="relative border-b py-10"
        style={cityRecord?.hero_image_url ? {
          backgroundImage: `url('${cityRecord.hero_image_url}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : undefined}
      >
        {cityRecord?.hero_image_url && <div className="absolute inset-0 bg-background/80" />}
        <div className="container relative z-10">
          <Button asChild variant="ghost" size="sm" className="mb-4 gap-1 -ml-2">
            <Link to="/">
              <ArrowLeft className="h-4 w-4" />
              Todas las salas
            </Link>
          </Button>
          <h1 className="font-display text-2xl font-bold tracking-tight md:text-4xl">
            Salas de Conciertos en {cityName}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
            Descubre las salas de conciertos en {cityName} con precios oficiales, precios aportados por la comunidad,
            aforo, servicios técnicos y reviews de bandas y promotores. Toda la información que necesitas para
            elegir la mejor sala para tu próximo bolo.{" "}
            <Link to="/" className="underline hover:text-foreground">Ver todas las salas</Link>.
          </p>
          {cityRecord?.hero_image_credit && (
            <p className="mt-1 text-xs text-muted-foreground/60">Foto: {cityRecord.hero_image_credit}</p>
          )}
        </div>
      </section>

      <section className="container py-8">
        {isLoading || cityLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-52 rounded-lg" />
            ))}
          </div>
        ) : data?.venues.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <p className="text-lg font-medium">No se encontraron salas en {cityName}</p>
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-semibold">Listado de salas en {cityName}</h2>
                <SortSelect value={sortBy} onChange={(v) => { setSortBy(v); setPage(0); }} />
              </div>
              <p className="text-sm text-muted-foreground">
                {data?.totalCount} sala{data?.totalCount !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data?.venues.map((venue) => (
                <VenueCard key={venue.id} venue={venue} />
              ))}
            </div>

            {data && data.totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <span className="px-3 text-sm text-muted-foreground">
                  Página {page + 1} de {data.totalPages}
                </span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(data.totalPages - 1, p + 1))} disabled={page >= data.totalPages - 1}>
                  Siguiente <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </Layout>
  );
}
